import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";

type StoredSession = {
  access_token?: string;
  expires_at?: number;
  user?: unknown;
};

function readStoredUser(): unknown | null {
  if (typeof window === "undefined") return null;
  try {
    const preferredKey = import.meta.env.VITE_SUPABASE_PROJECT_ID
      ? `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`
      : null;
    const keys = [
      ...(preferredKey ? [preferredKey] : []),
      ...Array.from({ length: window.localStorage.length }, (_, i) => window.localStorage.key(i)).filter(
        (key): key is string => !!key && key.startsWith("sb-") && key.endsWith("-auth-token") && key !== preferredKey,
      ),
    ];

    for (const key of keys) {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const session = JSON.parse(raw) as StoredSession;
      const expiresAt = typeof session.expires_at === "number" ? session.expires_at : 0;
      if (session.access_token && session.user && expiresAt * 1000 > Date.now() - 60_000) {
        return session.user;
      }
    }
  } catch {
    // Ignore malformed storage and let the auth client decide below.
  }
  return null;
}

async function getSessionWithTimeout() {
  return Promise.race([
    supabase.auth.getSession(),
    new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 1500)),
  ]);
}

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  beforeLoad: async () => {
    // Do not let the auth client block protected routes indefinitely while it
    // restores/refreshes a browser session. The server functions still verify
    // the bearer token; this client gate only decides whether to show the page
    // shell or send clearly signed-out users to /auth.
    const storedUser = readStoredUser();
    if (storedUser) return { user: storedUser };

    const result = await getSessionWithTimeout();
    if (!result?.data.session?.user) throw redirect({ to: "/auth" });
    return { user: result.data.session.user };
  },
  component: () => <Outlet />,
});



